import { useQuery } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { apiFetchSearch } from '../../../dashboard/api';
import { useDispatch } from 'react-redux';
import { generateSearchString } from '../MessageDisplay/AssignMessageModal';
import { AbsenceProposalSearchResult } from '../../../../models/AbsenceProposal';
import { Contact } from '../../../../models/Contact';
import InformationBox from '@prio365/prio365-react-library/lib/InformationBox/components/InformationBox';
import { Classes } from 'jss';
import { ContactId, MessageId, ProjectId } from '../../../../models/Types';
import { apiAssignMessage, apiUnassignMessage } from '../../api';
import { useTranslation } from 'react-i18next';
import { updateMessage } from '../../actions/actionControllers/messageActionController';
import { notification } from 'antd';
import moment from 'moment';
import { Message } from '../../../../models/Message';
import ContactPickerPrioCL from '../../../contacts/components/ContactPickerPrioCL';
import { Button } from '@prio365/prio365-react-library';

interface AssignMailInfoBoxProps {
  classes: Classes;
  projectId: ProjectId;
  message: Message;
  assignedToContact: Contact;
  loading: boolean;
  assignmentPopoverVisible: boolean;
  setAssignmentPopoverVisible: (visible: boolean) => void;
}

export const AssignMailInfoBox: React.FC<AssignMailInfoBoxProps> = (props) => {
  //#region ------------------------------ Defaults
  const {
    classes,
    projectId,
    message,
    assignedToContact,
    loading,
    assignmentPopoverVisible,
    setAssignmentPopoverVisible,
  } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const messageId = message.id;

  const searchString = generateSearchString();

  const { data: _publicAbsenceProposals } = useQuery({
    queryKey: ['publicAbsenceProposals'],
    queryFn: () =>
      apiFetchSearch(
        'publicAbsenceProposals',
        encodeURIComponent(searchString),
        1000,
        false
      ),
    staleTime: 1000 * 60 * 15, // 15 min
    enabled: !loading && assignmentPopoverVisible,
  });

  const publicAbsenceProposals = useMemo(() => {
    if (_publicAbsenceProposals?.data) {
      return _publicAbsenceProposals.data
        .items as AbsenceProposalSearchResult[];
    }
    return [];
  }, [_publicAbsenceProposals]);

  const relevantAbsenceProposals = useMemo(() => {
    if (assignedToContact) {
      const absenceProposals = publicAbsenceProposals.filter(
        (searchResultItem) =>
          searchResultItem.data.applicantId === assignedToContact.contactId
      );
      return absenceProposals;
    }
    return [];
  }, [assignedToContact, publicAbsenceProposals]);

  const isAssignedToContactAbsent = useMemo(
    () => relevantAbsenceProposals.length > 0,
    [relevantAbsenceProposals]
  );

  const [contactPickerDisabled, setContactPickerDisabled] =
    useState<boolean>(false);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const assignMessage = async (
    projectId: ProjectId,
    messageId: MessageId,
    contactId?: ContactId
  ) => {
    if (projectId && messageId && contactId) {
      setContactPickerDisabled(true);
      const { data } = await apiAssignMessage(projectId, messageId, contactId);
      setContactPickerDisabled(false);
      if (data) {
        dispatch(updateMessage(projectId, messageId, data));
      } else {
        notification.open({
          message: t('common:error'),
          description: t('mail:errorMessages.messages.messageAssignmentError'),
        });
      }
    }
  };

  const unassignMessage = async (
    projectId: ProjectId,
    messageId: MessageId
  ) => {
    if (projectId && messageId) {
      setContactPickerDisabled(true);
      const { result } = await apiUnassignMessage(projectId, messageId);
      setContactPickerDisabled(false);
      if (result.status >= 200 && result.status < 300) {
        const {
          messageAssignmentId: _,
          ...customSingleValueExtendedProperties
        } = message.customSingleValueExtendedProperties;
        dispatch(
          updateMessage(projectId, message.id, {
            customSingleValueExtendedProperties: {
              ...customSingleValueExtendedProperties,
              messageAssignmentId: null,
            },
          })
        );
      } else {
        notification.open({
          message: t('common:error'),
          description: t(
            'mail:errorMessages.messages.messageUnassignmentError'
          ),
        });
      }
    }
  };

  const changeAssignedProjectMember = (value: string | string[]) => {
    let contactId: ContactId = null;
    Array.isArray(value) ? (contactId = value[0]) : (contactId = value);
    if (contactId) {
      assignMessage(projectId, messageId, contactId);
    } else {
      unassignMessage(projectId, messageId);
    }
  };
  //#endregion

  //#region ------------------------------ Effects
  //#endregion

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <div
        className={classes.flexRow}
        style={{
          alignItems: 'center',
          width: '300px',
        }}
      >
        <ContactPickerPrioCL
          value={assignedToContact?.contactId}
          projectId={projectId}
          onlyInternalProject
          disabled={contactPickerDisabled}
          multiple={false}
          onChange={(contact) => {
            const isNewContactAbsent = publicAbsenceProposals.some(
              (absenceProposal) => absenceProposal.data.applicantId === contact
            );
            changeAssignedProjectMember(contact);
            !isNewContactAbsent && setAssignmentPopoverVisible(false);
          }}
          size="default"
          hideShowMoreButton
        ></ContactPickerPrioCL>
        <Button
          type="link"
          iconProp={['fal', 'xmark']}
          tooltip={t('mail:messageDisplay.removeMessageAssignment')}
          onClick={() => {
            changeAssignedProjectMember(undefined);
            setAssignmentPopoverVisible(false);
          }}
        />
      </div>
      {assignedToContact && isAssignedToContactAbsent && (
        <div className={classes.infoBoxWrapper}>
          <InformationBox
            type="warning"
            description={t('mail:messageDisplay.assignMessageWarning', {
              firstName: assignedToContact.firstName,
              lastName: assignedToContact.lastName,
              absentTo: moment(relevantAbsenceProposals[0].data.to).format(
                'dd, DD. MMM'
              ),
            })}
            marginBottom="none"
          ></InformationBox>
        </div>
      )}
    </div>
  );
};

export default AssignMailInfoBox;
